import React from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'

import { ListTile, Spinner, Animation, Button } from 'components'
import { ItemBlock, SectionBlock, SortableForest, ItemInput, SectionInput } from 'features'
import { AccessModule } from 'modules'

import { Section, Item } from 'schema'
import { itemContext, sectionContext } from 'context'
import { filterCheck, sortItems, groupItems, checkPageLoaded } from 'utils'
import * as actionCreators from 'store/actions'
import { AnimatePresence } from 'framer-motion'

const Container = styled.div`
    border-radius: 5px;
    /* width: 20rem; */
    /* padding: 0.5rem; */
    background-color: var(--primary-color);
    height: 100%;
    padding: 0 0 0 0;
    /* display: flex;
    flex-direction: row; */
    display: grid;
    grid-template-columns: auto auto;
    justify-content: flex-start;
    grid-column-gap: 0.5rem;
    overflow-x: hidden;
    /* width: calc(100vw - 0.5rem); */
`

const SpinnerContainer = styled.div`
    padding-left: ${props => props.full_screen ? '2.5vw' 
                    : props.full_width ? 'var(--main-padding-width-small)' 
                    : 'var(--main-padding-width)'};
    padding-right: ${props => props.full_screen ? '2.5vw'  
                    : props.full_width ? 'var(--main-padding-width-small)' 
                    : 'var(--main-padding-width)'};
    margin-left: 0.5rem;
    @media (max-width: 1024px) {
        padding-left: ${props => props.full_screen ? '5vw' : 'var(--main-padding-width-small)'};
        padding-right: ${props => props.full_screen ? '5vw' : 'var(--main-padding-width-small)'};
    }
`

const KanbanView = props => {
    const {

    } = props
    const debug = false;
    const start_time = performance.now()
    const dispatch = useDispatch()
    const data = useSelector(state => state.db.present.data && state.db.present.data.toJS(), (newValue) => (Object.keys(newValue['items']).length === Object.keys(data['items']).length))
    // const data = useSelector(state => state.db.present.data && state.db.present.data.toJS(), () => (true))
    const user = useSelector(state => state.auth.user, (newValue) => (newValue['activePageID'] === user['activePageID']))
    const inputID = useSelector(state => state.content.inputID)
    const modalID = useSelector(state => state.content.modalID)
    const sessionId = useSelector(state => state.content.sessionId)
    const showAccess = useSelector(state => state.content.showAccess)
    const loadedPages = useSelector(state => state.db.present.loadedPages)
    const pageID = user['activePageID']
    const page = data['pages'][pageID]
    const deferredLoaded = useSelector(state => state.db.present.deferredLoaded)
    const deferred = useSelector(state => state.db.present.deferred)
    const isPageLoaded = checkPageLoaded(pageID, user, loadedPages)
    const [sectionLoading, setSectionLoading] = React.useState({})
    console.log({inputID})

    const filters = page['filters']
    const sort = page['sort']
    const group = page['group']
    const isFiltered = filters && Object.values(filters).some(value => value)
    const isSorted = sort && sort['value'] && sort['direction']
    const isGrouped = group && Object.keys(group).length > 0
    const disableDND = isFiltered || isSorted || isGrouped || inputID || modalID || !deferredLoaded
    const limit = 20
    const [sectionLimits, setLimits] = React.useState(() => {
        let defaultLimits = {}

        page['sections'].forEach(sectionID => { defaultLimits[sectionID] = limit })
        return defaultLimits;
    })
    
    React.useEffect(() => {
        const check = checkPageLoaded(pageID, user, loadedPages)
        if(!check['loaded'] && deferredLoaded){
            console.log('[KANBAN_VIEW] Need to query page data', check['toLoad'])
            dispatch(actionCreators.loadPage(pageID, check['toLoad'].join(','), data))
        }
    }, [pageID, user['show_completed']])

    const renderDragOverlay = (id) => {
        return {
            item: <ItemBlock itemID={id} type='DRAG_OVERLAY' disableBottomBorder/>,
            container: <SectionBlock section={data['sections'][id]} type='DRAG_OVERLAY'/>
        }
    }

    const onDrop = (values) => {
        if(values['type'] === 'ITEM'){
            dispatch(actionCreators.commitDND2({...values, data, userId: user['uid'], sessionId}))
        } else if (values['type'] === 'CONTAINER') {
            let updatedPage = {...page}
            updatedPage['sections'] = values['containers']
            dispatch(actionCreators.updatePage(updatedPage, data, user['uid']))
        } else if (values['type'] === 'CALENDAR') {
            const schedule = values['schedule']
            let updatedItem = new Item({
                ...data['items'][values['activeId']],
                start_date: schedule['start_date'],
                end_date: schedule['end_date'],
                start_time: schedule['view'] !== 'month' ? schedule['start_time'] : null,
                end_time: schedule['view'] !== 'month' ? schedule['end_time'] : null,
            })
            console.log('[LISTVIEW]', updatedItem)
            dispatch(actionCreators.updateItem(updatedItem, data, user['uid'], sessionId))
        }
    }

    const buildView = () => {
        const listID = 'uuidv4()'
        
        // Get sections
        let sectionIDs = page['sections'].filter(sectionID => data['sections'][sectionID] && data['sections'][sectionID]['status'] === sectionContext.STATUS.ACTIVE)
        user['show_archived'] && sectionIDs.push(...page['sections'].filter(sectionID => data['sections'][sectionID]['status'] === sectionContext.STATUS.ARCHIVE))
        const sections = sectionIDs.map(sectionID => data['sections'][sectionID])

        // Construct items
        let trees = []
        if(sections.length > 0){
            sections.forEach((section, index) => {
                if(!section['items_visibility']){
                    trees.push({
                        id: section['sectionID'],
                        tree: [],
                        beforeTree: <div style={{marginBottom: '0.5rem'}}><SectionBlock section={section}/></div>,
                        afterTree: [],
                        block: <SectionBlock hideGrip section={section}/>,
                        disableDrag: disableDND || section['status'] === sectionContext.STATUS.ARCHIVE
                    })
                } else {
                    // Render items
                    let blockCount = 0
                    let itemCount = 0
                    let lastParents = []
                    let afterTree = []
                    const itemStatuses = process.env.REACT_APP_DISABLE_COMPLETED ? [itemContext.STATUS.INCOMPLETE]
                                        : user['show_completed'] ? [itemContext.STATUS.INCOMPLETE, itemContext.STATUS.COMPLETED] 
                                        : [itemContext.STATUS.INCOMPLETE]
                    let items = section['items']['INCOMPLETE'].map(itemID => data['items'][itemID])
                    user['show_completed'] && !process.env.REACT_APP_DISABLE_COMPLETED && items.push(...section['items']['COMPLETED'].map(itemID => data['items'][itemID]))
                    let filteredItems = []
                    itemStatuses.forEach(status => {
                        let statusItems = items.filter(item => {
                            if(!item) { return false }
                            if(item['status'] !== status) { return false }
                            itemCount += 1
                            const filterInclude = !filters ? true 
                                                : Object.keys(filters).length === 0 ? true
                                                : filterCheck(filters, item)
                            const toInclude = blockCount + 1 > sectionLimits[section['sectionID']] ? false
                                                : (item['status'] === itemContext.STATUS.INCOMPLETE && filterInclude) ||
                                                (filterInclude && user['show_completed'] 
                                                && item['status'] !== itemContext.STATUS.INCOMPLETE 
                                                && blockCount < sectionLimits[section['sectionID']])
                            if(toInclude && status === itemContext.STATUS.INCOMPELTE){
                                lastParents = item['parents']
                            }
                            
                            if(toInclude){
                                blockCount += 1
                            }
                            return toInclude
                        })
        
                        if(sort && sort['value']){
                            statusItems = sortItems(sort, statusItems)
                        }
                        filteredItems.push(...statusItems)
                    })
        
                    const tree = filteredItems.map(item => {
                        const disableDrag = disableDND || inputID === `${item['itemID']}_ListView_${listID}` || item['status'] === itemContext.STATUS.COMPLETED
                        return {
                            id: item['itemID'],
                            parentId: item['parents'] && item['parents'].slice(-1)[0],
                            block: <ItemBlock itemID={item['itemID']} key={item['itemID']} type='KANBAN' primaryColor='var(--secondary-color)' disableBottomBorder/>,
                            depth: item['parents'].length,
                            disableDrag: disableDrag,
                            parents: item['parents'],
                            containerId: section['sectionID']
                        }
                    })
                    
                    // Render ItemInput
                    const sectionLimit = sectionLimits[section['sectionID']]
                    // const showSpinner = (deferred && deferred.sectionIDs && deferred.sectionIDs.includes(section['sectionID'])) | (!deferredLoaded)
                    const showSpinner = (deferred && deferred.sectionIDs && deferred.sectionIDs.includes(section['sectionID']))
                    afterTree.push(
                        <div>
                            {showSpinner ? <Spinner styles={{width: '100%', margin: '0.5rem 0 0 0', color: 'var(--secondary-color)'}}/> : null}
                            {blockCount === sectionLimit && itemCount > sectionLimit &&
                            <Button 
                                text={`Show ${itemCount - sectionLimit < limit ? itemCount - sectionLimit : 20} more`}
                                styles={{margin: '0.5rem 0.1rem', backgroundColor: 'var(--secondary-color)'}}
                                onSelect={() => {
                                    setLimits({...sectionLimits, [section['sectionID']]: sectionLimit + limit })
                                }}
                                />}
                            <ItemInput id={`ItemInput_${section['sectionID']}`} 
                                key={section['sectionID'] + '_ItemInput'}  
                                pageID={pageID} sectionID={section['sectionID']} 
                                parents={lastParents} enableTab/>
                        </div>
                    )
        
                    trees.push({
                        id: section['sectionID'],
                        visibility: section['items_visibility'],
                        tree: tree,
                        beforeTree: <div style={{marginBottom: '0.5rem'}}><SectionBlock section={section}/></div>,
                        afterTree: afterTree,
                        block: <SectionBlock hideGrip section={section}/>,
                        disableDrag: disableDND || section['status'] === sectionContext.STATUS.ARCHIVE
                        // disableDrag: index === 0
                    })
        
                    {/* <SectionInput id={section['sectionID'] + '_SectionInput'} key={section['sectionID'] + '_SectionInput'}/> */}
                }
            })
        } 

        debug && console.log(`[KANBAN_VIEW] Build vieww ${((performance.now() - start_time)/1000).toFixed(2)}s`)
        const disableVirtual = true
        return <SortableForest id='KANBAN_FOREST' key='KANBAN_FOREST'
                    trees={trees} dragOverlay={renderDragOverlay} onDrop={onDrop} 
                    direction='HORIZONTAL' disableNesting disableVirtualWindowScroll 
                    disableVirtual={disableVirtual}
                    disableDND={disableDND}
                    // autoScroll={!showAccess}
                    secondaryContainer={<AccessModule/>} 
                    showSecondary={showAccess}
                    afterForest={!isFiltered && !isSorted && !isGrouped && 
                    <div style={{width: '20rem', marginRight: '2rem', marginLeft: trees.length === 0 && '2rem'}}>
                        <SectionInput id={`${page.sections.slice(-1)[0]}_SectionInput`} 
                            type='BUTTON' base='VISIBLE'
                            styles={{height: 'calc(100% - 1rem)', margin: '0 1rem 0 1rem'}}/>
                    </div>}
                    containerStyles={{ margin: 0, padding: '0rem 1.5rem 1rem 1.5rem', gap: '2rem', width: '20rem', height: 'auto' }}
                    treeStyles={{ maxHeight: 'calc(100vh - 24rem)', overflowY: 'auto', height: !disableVirtual ? '100vh' : 'auto' }}
                    />
    }

    const buildGroupView = () => {
        const pageGroup = data['pages'][pageID]['group']
        const groups = groupItems(pageID, data, user)
        const statuses = process.env.REACT_APP_DISABLE_COMPLETED ? ['INCOMPLETE']
                        : user['show_completed'] ? ['INCOMPLETE','COMPLETED'] : ['INCOMPLETE']

        const trees = Object.values(groups).map(group => {
            const items = [...group['INCOMPLETE'], ...group['COMPLETED']]
            let tree = []
            let blockCount = 0
            let itemCount = 0
            const sectionLimit = sectionLimits[group['id']] || 20

            items.forEach(item => {
                itemCount += 1
                if(statuses.includes(item['status']) && blockCount + 1 <= sectionLimit){
                    blockCount += 1
                    tree.push({
                        id: item['itemID'],
                        parentId: item['parents'] && item['parents'].slice(-1)[0],
                        block: <ItemBlock type='KANBAN' itemID={item['itemID']} disableDrag={true} primaryColor='var(--secondary-color)'/>,
                        depth: item['parents'].length,
                        disableDrag: true,
                        parents: item['parents'],
                        containerId: group['id']
                    }) 
                }
            })

            return {
                id: group['id'],
                containerId: pageID,
                disableDrag: true,
                tree: tree,
                beforeTree: <ListTile text={group['title']} icon={group['icon']} 
                                styles={{ 
                                    height: '2.5rem', backgroundColor: 'transparent', 
                                    borderBottom: '1px solid var(--primary-accent-color)', borderRadius: 0 
                                }}/>,
                afterTree: 
                <div>
                    {!deferredLoaded ? <Spinner styles={{width: '100%', margin: '0.5rem 0 0 0', color: 'var(--secondary-color)'}}/> : null}
                    {blockCount === sectionLimit && itemCount > sectionLimit &&
                    <Button 
                        text={`Show ${itemCount - sectionLimit < limit ? itemCount - sectionLimit : 20} more`}
                        styles={{margin: '0.5rem 0.1rem', backgroundColor: 'var(--secondary-color)'}}
                        onSelect={() => {
                            setLimits({...sectionLimits, [group['id']]: sectionLimit + limit })
                        }}
                        />}
                    <ItemInput id={`ItemInput_${group['id']}`} sectionSelector
                            pageID={pageID} 
                            sectionID={page['sections'][0]} 
                            parents={[]} 
                            priority={pageGroup && pageGroup === 'PRIORITY' ? group['id'] : null}
                            tags={pageGroup && pageGroup === 'TAGS' ? [group['id']] : null}
                            start_date={pageGroup && pageGroup === 'START_DATE' ? group['id'] : null}
                            end_date={pageGroup && pageGroup === 'END_DATE' ? group['id'] : null}/>
                </div>
            }
        })
        return <SortableForest key='GROUP' trees={trees} dragOverlay={renderDragOverlay} onDrop={onDrop} 
                    direction='HORIZONTAL' 
                    disableVirtual={false} disableDND disableNesting disableVirtualWindowScroll 
                    secondaryContainer={<AccessModule/>} showSecondary={showAccess}
                    styles={{ margin: '0 0rem 2rem 1.5rem', padding: '0.5rem 0.5rem 0rem 0.5rem', treeWidth: '20rem', treeHeight: 'auto' }}
                    treeStyles={{ maxHeight: 'calc(100vh - 21rem)', overflowY: 'auto', height: '100vh' }}
                    />      
    }
    
    if(isPageLoaded['loaded'] || !deferredLoaded){
        return (
            <Container key='listview'>
                <AnimatePresence>
                    <Animation animation='default' duration={0.5}>
                    {page['group'] ? buildGroupView() : buildView()}
                    </Animation>
                </AnimatePresence>
            </Container>
        )
    } else {
        return (
            <SpinnerContainer key='listview' full_width={true} full_screen={true}>
                <Spinner/>
            </SpinnerContainer>
        )
    }
}

export default KanbanView